import type { Injector } from '@angular/core';
import { Injectable } from '@angular/core';
import type { SafeHtml } from '@angular/platform-browser';

import type { Observable } from 'rxjs';

import { I18nService } from '@recruitee/i18n';
import type { IntegrationKinds } from '@recruitee/integrations-types';
import { DialogService } from '@recruitee/user-interface';

import { UrlInTranslationsPipe } from '../../common';
import type { LegalDialogConfig } from '../types';
import type { IntegrationListLegalDialogData } from './integration-list-legal-dialog.component';
import {
  IntegrationListLegalDialogComponent,
  IntegrationListLegalDialogConfig,
} from './integration-list-legal-dialog.component';

@Injectable({ providedIn: 'root' })
export class IntegrationLegalDialogService {
  public legalDialogButtonText = this.i18n.translate('rti.common.settings.legal_dialog.accept');

  public legalDialogTextOverClaims = this.i18n.translate(
    'rti.common.settings.legal_dialog.paragraph_1',
    {
      buttonText: this.legalDialogButtonText,
    },
  );

  constructor(
    private i18n: I18nService,
    private urlInTranslationsPipe: UrlInTranslationsPipe,
    private dialogService: DialogService,
  ) {}

  public openLegalDialog(
    kind: IntegrationKinds | null,
    partnerName: string | null,
    injector: Injector | undefined,
    legalDialogConfig: LegalDialogConfig,
  ): Observable<{ accepted: boolean } | undefined> {
    const legalDialogData: IntegrationListLegalDialogData = {
      partnerName: partnerName || this.i18n.translate(`rti.${kind}.name`),
      helpArticleUrl: legalDialogConfig.helpArticleUrl,
      textOverClaims: legalDialogConfig.textOverClaims || this.legalDialogTextOverClaims,
      claimsHeading: legalDialogConfig.claimsHeading,
      claims: legalDialogConfig.claims || this.legalDialogClaims(kind, partnerName),
      textUnderClaims:
        legalDialogConfig.textUnderClaims || this.legalDialogTextUnderClaims(legalDialogConfig),
      buttonText: legalDialogConfig.buttonText || this.legalDialogButtonText,
      listType: legalDialogConfig.listType || 'unordered',
    };

    return this.dialogService.openCustom(
      IntegrationListLegalDialogComponent,
      { ...IntegrationListLegalDialogConfig, injector },
      legalDialogData,
    ).afterClosed;
  }

  public legalDialogClaims(kind: IntegrationKinds | null, name: string | null): SafeHtml[] {
    const partnerName = name || this.i18n.translate(`rti.${kind}.name`);
    return [
      this.i18n.translate('rti.common.settings.legal_dialog.list.1'),
      this.i18n.translate('rti.common.settings.legal_dialog.list.2', { partner_name: partnerName }),
      this.i18n.translate('rti.common.settings.legal_dialog.list.3', { partner_name: partnerName }),
    ];
  }

  public legalDialogTextUnderClaims(legalDialogConfig: LegalDialogConfig): SafeHtml {
    return this.urlInTranslationsPipe.transform(
      this.i18n.translate('rti.common.settings.legal_dialog.paragraph_2'),
      { a: 'target=_blank href=' + legalDialogConfig.helpArticleUrl },
    );
  }
}
